'use client'

import { useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'

import { stegaClean } from 'next-sanity'
import { PortableTextBlock } from 'next-sanity'

import { Turnstile, TurnstileInstance } from '@marsidev/react-turnstile'

import { sendContactEmail } from '@/app/actions/sendContactEmail'
import { BackToHomeButton } from '@/app/components/BackToHomeButton'
import { Button, buttonSizeClasses } from '@/app/components/Button'
import { Input, Textarea } from '@/app/components/Form'
import PortableTextMinimal from '@/app/components/PortableTextMinimal'
import { TRACKING_LOCATIONS, TrackingLocation } from '@/app/utils/analytics'
import { isProductionDomain } from '@/app/utils/captcha'
import { cn } from '@/app/utils/style'
import { ContactForm as ContactFormRaw } from '@/sanity.types'
import { dataAttr } from '@/sanity/lib/utils'

export type ContactFormProps = {
  contactForm: ContactFormRaw & { _key?: string }
  tracking: TrackingLocation
  className?: string
  documentId?: string
  documentType?: string
  parentPath?: string
}

type ContactFormData = {
  name: string
  email: string
  message: string
}

export const ContactForm = ({
  contactForm,
  tracking,
  className,
  documentId,
  documentType,
  parentPath,
}: ContactFormProps) => {
  const turnstileRef = useRef<TurnstileInstance>(null)
  const captchaSiteKey = process.env.NEXT_PUBLIC_CF_CAPTCHA_SITE_KEY
  const [isCaptchaEnabled, setIsCaptchaEnabled] = useState(false)
  const [captchaToken, setCaptchaToken] = useState('')
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [submitError, setSubmitError] = useState<string>('')

  useEffect(() => {
    setIsCaptchaEnabled(isProductionDomain())
  }, [])

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<ContactFormData>({
    mode: 'onSubmit',
    defaultValues: {
      name: '',
      email: '',
      message: '',
    },
  })

  const thankYouMessage =
    stegaClean(contactForm.thankYouMessage) ||
    "We've received your message and our support team will be in touch soon."

  const attr =
    contactForm._key && documentId && documentType && parentPath
      ? dataAttr({
          id: documentId,
          type: documentType,
          path: `${parentPath}[_key=="${contactForm._key}"]`,
        })
      : undefined

  const resetCaptcha = () => {
    setCaptchaToken('')
    turnstileRef.current?.reset()
  }

  const onSubmit = async (data: ContactFormData) => {
    setSubmitError('')

    try {
      const recipient = stegaClean(contactForm.recipient) || 'support@paved.com'

      const result = await sendContactEmail({
        ...data,
        recipient,
        captchaToken,
      })

      if (result.success) {
        setIsSubmitted(true)
        reset()
        resetCaptcha()
      } else {
        setSubmitError(result.error || 'Failed to submit form. Please try again.')
        resetCaptcha()
      }
    } catch {
      setSubmitError('Failed to submit form. Please try again.')
      resetCaptcha()
    }
  }

  return (
    <div
      className={cn(
        'w-full max-w-[434px] mx-auto my-2xl',
        isSubmitted ? 'lg:max-w-[1144px]' : 'lg:max-w-[660px]',
        className,
      )}
      {...(attr && { 'data-sanity': attr.toString() })}
    >
      {!isSubmitted && (contactForm.heading || contactForm.subheading) && (
        <div className="flex flex-col gap-sm mb-6xl items-center">
          <>
            {contactForm.heading && (
              <p className="size-header-h3 font-normal text-white-950 lg:size-header-h2 lg:font-normal">
                <PortableTextMinimal
                  value={contactForm.heading as PortableTextBlock[]}
                  tracking={{ location: TRACKING_LOCATIONS.PAGE_CONTENT }}
                />
              </p>
            )}
            {contactForm.subheading && (
              <p className="size-body-md text-white-900 [&_a]:text-green-500">
                <PortableTextMinimal
                  value={contactForm.subheading as PortableTextBlock[]}
                  tracking={{ location: TRACKING_LOCATIONS.PAGE_CONTENT }}
                  enableMailtoLinks
                />
              </p>
            )}
          </>
        </div>
      )}

      {isSubmitted ? (
        <div className="flex flex-col gap-sm mb-6xl items-center">
          <>
            {contactForm.heading && (
              <p className="size-header-h3 font-normal text-white-950 lg:size-header-h2 lg:font-normal">
                Your message has been sent
              </p>
            )}
            {contactForm.subheading && (
              <p className="size-body-md text-white-900">{thankYouMessage}</p>
            )}
            <div className="mt-6xl">
              <BackToHomeButton />
            </div>
          </>
        </div>
      ) : (
        <form noValidate onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-xl">
          <div className="flex flex-col gap-xl">
            <Input
              {...register('name', {
                required: 'Name is required',
              })}
              type="text"
              error={errors.name?.message}
              disabled={isSubmitting}
              placeholder="Name"
            />
            <Input
              {...register('email', {
                required: 'Email is required',
                pattern: {
                  value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                  message: 'Please enter a valid email address',
                },
              })}
              type="email"
              error={errors.email?.message}
              disabled={isSubmitting}
              placeholder="Email"
            />
            <Textarea
              {...register('message', {
                required: 'Message is required',
              })}
              error={errors.message?.message}
              disabled={isSubmitting}
              rows={6}
              placeholder="How can we help?"
            />
          </div>

          {isCaptchaEnabled && captchaSiteKey && (
            <div className="flex justify-center">
              <Turnstile
                ref={turnstileRef}
                siteKey={captchaSiteKey}
                onSuccess={setCaptchaToken}
                onError={() => setCaptchaToken('')}
                onExpire={() => setCaptchaToken('')}
                options={{
                  theme: 'light',
                  size: 'flexible',
                  appearance: 'interaction-only',
                }}
              />
            </div>
          )}

          {submitError && <p className="size-body-sm text-red-500 font-medium">{submitError}</p>}

          <div className="text-center">
            <Button
              button={{
                _type: 'button',
                variant: 'primaryDark',
                size: 'md',
                link: undefined,
                icon: undefined,
                iconPlacement: 'none',
                dataId: null,
              }}
              className={cn(buttonSizeClasses({ lg: 'md' }), 'mx-auto')}
              tracking={tracking}
              type="submit"
              disabled={isSubmitting || (isCaptchaEnabled && !captchaToken)}
            >
              {isSubmitting ? 'Submitting...' : 'Submit'}
            </Button>
          </div>
        </form>
      )}
    </div>
  )
}
